import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { cloneDeep } from 'lodash-es';
import { MyInfo } from '@/common/api/resTypes/userInfoRes';
import { RoleIdMap, type RoleId } from '@/common/auth/auth.defines';
import {
  TIMEZONE_ITEMS,
  USER_PROFILE_IMAGE,
  USER_PROFILE_IMAGE_DEFAULT,
} from '@/common/utils/define';
import { webStorageController } from '@/common/utils/browser';
import { normalizeUserLanguage, setUserLanguage } from '@/common/locale';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { getV7Me } from '@/api-client/userAndPermission/queries/user-v7-controller/user-v7-controller';
import { UserWithGroupsResponseTwoFactorType } from '@/api-client/meta/model';
import type {
  UserWithGroupsAndRoleV7Response,
  UserGroupInfoV7Item,
} from '@/api-client/userAndPermission/model';

dayjs.extend(utc);
dayjs.extend(timezone);

export type BaseInfo = Pick<MyInfo, 'email' | 'profileImage' | 'rolePermissionId' | 'activeId'>;

type NormalizedMyInfo = Omit<MyInfo, 'language' | 'role' | 'timezone'> & {
  language: ReturnType<typeof normalizeUserLanguage>;
  role: RoleId;
  timezone: MyInfo['timezone'];
};

export const INIT_MY_INFO: NormalizedMyInfo = {
  userId: -1,
  name: '',
  activeId: '',
  email: '',
  company: '',
  department: '',
  position: '',
  language: 'EN',
  phone: '',
  timezone: 'Asia/Seoul',
  utcTimeOffset: '',
  slack: '',
  profileImage: '',
  tenantOwned: null,
  groups: [],
  theme: 'light',
  role: RoleIdMap.User,
  rolePermissionId: '',
  roleName: '',
  onSiteEnabled: true,
  emailEnabled: false,
  smsEnabled: false,
  passwordChangeType: '',
  loginPopup: true,
  employeeId: '',
  ssoId: '',
  enablePassword: false,
  rolePermissionName: '',
  ipRules: [],
  ipRuleEnabled: false,
  secret: false,
  sessionTimeout: 0,
  twoFactorType: UserWithGroupsResponseTwoFactorType.NONE,
};

const normalizeRoleId = (role?: string): RoleId => {
  if (role && Object.values(RoleIdMap).includes(role as RoleId)) {
    return role as RoleId;
  }
  return RoleIdMap.User;
};

const normalizeTimezone = (timezone?: string): NormalizedMyInfo['timezone'] => {
  if (!timezone) return INIT_MY_INFO.timezone;
  const isKnownTimezone = TIMEZONE_ITEMS.some((item) => item.value === timezone);
  return (isKnownTimezone ? timezone : INIT_MY_INFO.timezone) as NormalizedMyInfo['timezone'];
};

const normalizeGroups = (groups?: UserGroupInfoV7Item[]): MyInfo['groups'] => {
  return (groups ?? [])
    .filter((group): group is UserGroupInfoV7Item & { groupId: number } => group.groupId != null)
    .map((group) => ({
      groupId: group.groupId,
      groupUserID: group.groupUserId,
      name: group.name,
      fixed: group.fixed,
      alert: group.alert,
      email: group.email,
      sms: group.sms,
      slack: group.slack,
    }));
};

const normalizeMyInfo = (data?: UserWithGroupsAndRoleV7Response): NormalizedMyInfo => {
  if (!data) return cloneDeep(INIT_MY_INFO);

  const dataExtra = data as Partial<MyInfo>;

  return {
    userId: data.userId ?? INIT_MY_INFO.userId,
    name: data.name ?? INIT_MY_INFO.name,
    activeId: data.activeId ?? INIT_MY_INFO.activeId,
    email: data.email ?? INIT_MY_INFO.email,
    company: data.company ?? INIT_MY_INFO.company,
    department: data.department ?? INIT_MY_INFO.department,
    position: data.position ?? INIT_MY_INFO.position,
    phone: data.phone ?? INIT_MY_INFO.phone,
    roleName: data.roleName ?? INIT_MY_INFO.roleName,
    theme: data.theme ?? INIT_MY_INFO.theme,
    utcTimeOffset: data.utcTimeOffset ?? INIT_MY_INFO.utcTimeOffset,
    slack: data.slack ?? INIT_MY_INFO.slack,
    profileImage: data.profileImage ?? INIT_MY_INFO.profileImage,
    passwordChangeType: data.passwordChangeType ?? INIT_MY_INFO.passwordChangeType,
    emailEnabled: data.emailEnabled ?? INIT_MY_INFO.emailEnabled,
    smsEnabled: data.smsEnabled ?? INIT_MY_INFO.smsEnabled,
    onSiteEnabled: data.onSiteEnabled ?? INIT_MY_INFO.onSiteEnabled,
    loginPopup: data.loginPopup ?? INIT_MY_INFO.loginPopup,
    enablePassword: data.passwordEnabled ?? INIT_MY_INFO.enablePassword,
    employeeId: data.employeeId ?? INIT_MY_INFO.employeeId,
    sessionTimeout: data.sessionTimeout ?? INIT_MY_INFO.sessionTimeout,
    rolePermissionId: data.rolePermissionId ?? INIT_MY_INFO.rolePermissionId,
    rolePermissionName: data.rolePermissionName ?? INIT_MY_INFO.rolePermissionName,
    tenantOwned: data.tenantOwned ?? INIT_MY_INFO.tenantOwned,
    locked: data.locked,
    language: normalizeUserLanguage(data.language),
    role: normalizeRoleId(data.role),
    timezone: normalizeTimezone(data.timezone),
    groups: normalizeGroups(data.groups),
    // Note: response 없는 필드인데 혹시나해서 추가.
    ipRules: dataExtra.ipRules ?? INIT_MY_INFO.ipRules,
    ipRuleEnabled: dataExtra.ipRuleEnabled ?? INIT_MY_INFO.ipRuleEnabled,
    twoFactorType: dataExtra.twoFactorType ?? INIT_MY_INFO.twoFactorType,
    ssoId: dataExtra.ssoId ?? INIT_MY_INFO.ssoId,
    secret: dataExtra.secret ?? INIT_MY_INFO.secret,
  };
};

export const useMyInfoStore = defineStore('myInfo', () => {
  const myInfo = ref<NormalizedMyInfo>(cloneDeep(INIT_MY_INFO));
  const timezone = ref<NormalizedMyInfo['timezone']>(INIT_MY_INFO.timezone);
  const isShowWindow = ref<boolean>(false);

  const selectedBaseInformation = computed<BaseInfo>(() => ({
    email: myInfo.value.email,
    profileImage: myInfo.value.profileImage,
    rolePermissionId: myInfo.value.rolePermissionId,
    activeId: myInfo.value.activeId,
  }));
  const inviteInfo = computed(() => ({
    userId: myInfo.value.userId,
    userName: myInfo.value.name,
  }));

  const initAccountInfo = () => {
    myInfo.value = cloneDeep(INIT_MY_INFO);
    timezone.value = INIT_MY_INFO.timezone;
    isShowWindow.value = false;
  };

  const setAccountInfo = (info: NormalizedMyInfo) => {
    myInfo.value = info;
    timezone.value = info.timezone;
    if (info) {
      if (!USER_PROFILE_IMAGE.includes(myInfo.value.profileImage)) {
        myInfo.value.profileImage = USER_PROFILE_IMAGE_DEFAULT;
      }
      if (info?.timezone) {
        dayjs.tz.setDefault(info.timezone);
      }
    }
  };

  const setIsShowWindow = (flag: boolean) => {
    isShowWindow.value = flag;
  };

  const setInviteInfo = ({ userId, userName }: { userId: number; userName: string }) => {
    myInfo.value.userId = userId;
    myInfo.value.name = userName;
  };

  const dispatchMyInfo = async () => {
    const { data } = await getV7Me(
      {},
      {
        frameName: FRAME_NAMES.CONFIG_MY_INFO.INFORMATION,
      },
    );
    const accountInfo = normalizeMyInfo(data);
    setAccountInfo(accountInfo);
    webStorageController.setItem({
      type: 'local',
      key: 'language',
      value: accountInfo.language,
    });
    setUserLanguage(accountInfo.language);
  };

  return {
    myInfo,
    timezone,
    isShowWindow,
    selectedBaseInformation,
    inviteInfo,
    initAccountInfo,
    setAccountInfo,
    setIsShowWindow,
    setInviteInfo,
    dispatchMyInfo,
  };
});
